/**
 * SPEC §11 – urenregistratie: timer in de bovenbalk, doel zoeken, starten, stoppen en tijd toevoegen.
 */
import { Icoon } from './Icoon';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fmtDuur, itemStand, leesDuur, vandaag, type ItemTijd } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useIk } from '../lib/data';
import { Badge, Modal, Veld } from './ui';

export interface Doel {
  soort: 'ITEM' | 'WERKPAKKET' | 'TAAK';
  id: string;
  label: string;
  sub: string;
  projectId: string | null;
  projectNummer: string | null;
  projectNaam: string | null;
}
export interface Timer {
  id: string;
  start: string;
  itemId: string | null;
  omschrijving: string | null;
  doel: Doel | null;
}

export const SOORT_LABEL: Record<Doel['soort'], string> = { ITEM: 'item', WERKPAKKET: 'werkpakket', TAAK: 'taak' };
export const doelTekst = (d: Doel) => `${d.projectNummer ? `${d.projectNummer} · ` : ''}${d.label}`;

/** Lopende timer van de ingelogde engineer; ververst elke minuut. */
export function useTimer() {
  const ik = useIk().data;
  return useQuery({ queryKey: ['timer'], queryFn: () => api.get<{ timer: Timer | null }>('/api/tijd/timer'), enabled: !!ik?.eid && !!ik.licentie?.modules.includes('UREN'), refetchInterval: 60000 });
}

/** Ververs alles wat met uren te maken heeft. */
export function useVerversUren() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['timer', 'week', 'agenda-uren', 'mijn-sprint', 'agile', 'data', 'rapport', 'goedkeuren']) void qc.invalidateQueries({ queryKey: [k] });
  };
}

/** Verstreken tijd die elke 20 seconden bijwerkt. */
function useVerstreken(start?: string) {
  const [nu, setNu] = useState(Date.now());
  useEffect(() => {
    if (!start) return;
    const t = setInterval(() => setNu(Date.now()), 20000);
    return () => clearInterval(t);
  }, [start]);
  return start ? Math.max(0, (nu - Date.parse(start)) / 60000) : 0;
}

/** SPEC §11.2 – timer in de bovenbalk. */
export function TimerKnop() {
  const ik = useIk().data;
  const q = useTimer();
  const [stop, setStop] = useState(false);
  const [start, setStart] = useState(false);
  const t = q.data?.timer;
  const min = useVerstreken(t?.start);
  if (!ik?.eid) return null;
  return (
    <>
      {t ? (
        <button className="timer-knop loopt" onClick={() => setStop(true)} title={`Timer loopt: ${t.doel ? doelTekst(t.doel) : ''}. Klik om te stoppen.`}>
          <Icoon naam="timer" maat={16} /> <b>{fmtDuur(min)}</b> <span className="timer-doel">{(t.doel?.label ?? '').slice(0, 22)}</span> <Icoon naam="square" maat={12} />
        </button>
      ) : (
        <button className="timer-knop" onClick={() => setStart(true)} title="Timer starten"><Icoon naam="timer" maat={16} /> <span className="timer-doel">Timer</span></button>
      )}
      {stop && t && <StopTimer timer={t} minuten={min} onSluit={() => setStop(false)} />}
      {start && <StartTimer onSluit={() => setStart(false)} />}
    </>
  );
}

/** Zoek een item, werkpakket of taak om tijd op te schrijven. */
export function DoelZoeker({ onKies, engineerId }: { onKies: (d: Doel) => void; engineerId?: string }) {
  const [q, setQ] = useState('');
  const r = useQuery({ queryKey: ['doelen', q, engineerId], queryFn: () => api.get<{ doelen: Doel[] }>(`/api/tijd/doelen?q=${encodeURIComponent(q)}${engineerId ? `&engineerId=${engineerId}` : ''}`) });
  const lijst = r.data?.doelen ?? [];
  return (
    <div className="kolom" style={{ gap: 6 }}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op project, item of taak…" />
      <div className="kolom" style={{ gap: 4, maxHeight: 320, overflow: 'auto' }}>
        {lijst.map((d) => (
          <button key={`${d.soort}:${d.id}`} className="knop" style={{ justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'normal' }} onClick={() => onKies(d)}>
            <span>
              <b>{d.label}</b>
              <span className="klein mute"> · {SOORT_LABEL[d.soort]}{d.sub && d.soort !== 'WERKPAKKET' ? ` · ${d.sub}` : ''}</span>
              <div className="klein mute">{d.projectNummer} {d.projectNaam}</div>
            </span>
          </button>
        ))}
        {r.data && !lijst.length && <div className="leeg">Niets gevonden. Je kunt tijd schrijven op items die je hebt opgepakt, op werkpakketten van je sprintteam en op je eigen taken.</div>}
      </div>
    </div>
  );
}

export function StartTimer({ onSluit, doel }: { onSluit: () => void; doel?: Doel }) {
  const ververs = useVerversUren();
  const [omschrijving, setOmschrijving] = useState('');
  const start = useActie((d: Doel) => api.post('/api/tijd/start', { doel: { soort: d.soort, id: d.id }, omschrijving: omschrijving || null }), { succes: 'Timer gestart', opNa: () => { ververs(); onSluit(); } });
  return (
    <Modal titel="Timer starten" onSluit={onSluit}>
      <div className="kolom" style={{ gap: 10 }}>
        <Veld label="Waar werk je aan? (optioneel)"><input value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} placeholder="Bijv. mapping HL7-berichten" /></Veld>
        {doel ? <button className="knop merk" onClick={() => start.mutate(doel)}>▶ Start op {doel.label}</button> : <DoelZoeker onKies={(d) => start.mutate(d)} />}
      </div>
    </Modal>
  );
}

/** SPEC §11.2 – stoppen: duur (kwartier), omschrijving, rest of naar Review. */
export function StopTimer({ timer, minuten, onSluit }: { timer: Timer; minuten: number; onSluit: () => void }) {
  const ververs = useVerversUren();
  const voorstel = Math.max(15, Math.round(minuten / 15) * 15);
  const [duur, setDuur] = useState(fmtDuur(voorstel));
  const [omschrijving, setOmschrijving] = useState(timer.omschrijving ?? '');
  const [rest, setRest] = useState('');
  const [review, setReview] = useState(false);
  const m = leesDuur(duur);
  const stop = useActie(
    () => api.post<{ verwijderd: boolean }>('/api/tijd/stop', { minuten: m ?? undefined, omschrijving: omschrijving || null, restUren: rest.trim() ? (leesDuur(rest) ?? 0) / 60 : undefined, review }),
    { succes: (r) => (r.verwijderd ? 'Timer verwijderd (0 minuten)' : 'Tijd geschreven'), opNa: () => { ververs(); onSluit(); } },
  );
  const isItem = timer.doel?.soort === 'ITEM';
  return (
    <Modal titel="Timer stoppen" onSluit={onSluit} voet={<><button className="knop" onClick={onSluit}>Laat lopen</button><button className="knop merk" disabled={m == null || stop.isPending} onClick={() => stop.mutate()}>Stoppen en opslaan</button></>}>
      <div className="kolom" style={{ gap: 10 }}>
        <div><b>{timer.doel?.label}</b> <span className="klein mute">{timer.doel?.projectNummer} {timer.doel?.projectNaam}</span></div>
        <div className="grid2">
          <Veld label="Duur (u:mm)" hint={`Gemeten ${fmtDuur(minuten)}; afgerond op een kwartier`}><input value={duur} onChange={(e) => setDuur(e.target.value)} /></Veld>
          {isItem && !review && <Veld label="Hoeveel heb je nog nodig?" hint="Leeg = schatting min besteed"><input value={rest} onChange={(e) => setRest(e.target.value)} placeholder="bijv. 3 of 2:30" /></Veld>}
        </div>
        <Veld label="Omschrijving"><input value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} /></Veld>
        {isItem && (
          <label className="check"><input type="checkbox" checked={review} onChange={(e) => setReview(e.target.checked)} /> Item is af: zet hem op Review</label>
        )}
      </div>
    </Modal>
  );
}

/** SPEC §11.2 – handmatig tijd toevoegen. */
export function TijdToevoegen({ doel, engineerId, onSluit }: { doel?: Doel; engineerId?: string; onSluit: () => void }) {
  const ververs = useVerversUren();
  const [gekozen, setGekozen] = useState<Doel | undefined>(doel);
  const [f, setF] = useState({ datum: vandaag(), duur: '1:00', omschrijving: '', rest: '', facturabel: true });
  const m = leesDuur(f.duur);
  const opslaan = useActie(
    () => api.post('/api/tijd/regels', { doel: { soort: gekozen!.soort, id: gekozen!.id }, datum: f.datum, minuten: m, omschrijving: f.omschrijving || null, facturabel: f.facturabel, restUren: f.rest.trim() ? (leesDuur(f.rest) ?? 0) / 60 : undefined, engineerId }),
    { succes: 'Tijd geschreven', opNa: () => { ververs(); onSluit(); } },
  );
  return (
    <Modal titel="Tijd toevoegen" onSluit={onSluit} voet={gekozen ? <><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop merk" disabled={!m || opslaan.isPending} onClick={() => opslaan.mutate()}>Opslaan</button></> : undefined}>
      {!gekozen ? (
        <DoelZoeker onKies={setGekozen} engineerId={engineerId} />
      ) : (
        <div className="kolom" style={{ gap: 10 }}>
          <div className="rij"><b className="groei">{gekozen.label} <span className="klein mute">{gekozen.projectNummer}</span></b>{!doel && <button className="knop klein" onClick={() => setGekozen(undefined)}>Ander doel</button>}</div>
          <div className="grid2">
            <Veld label="Datum"><input type="date" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} /></Veld>
            <Veld label="Duur (u:mm of uren)" hint={m == null ? 'Ongeldige duur' : `${fmtDuur(m ?? 0)} (op een kwartier)`}><input value={f.duur} onChange={(e) => setF({ ...f, duur: e.target.value })} /></Veld>
            {gekozen.soort === 'ITEM' && <Veld label="Nog nodig (optioneel)"><input value={f.rest} onChange={(e) => setF({ ...f, rest: e.target.value })} placeholder="bijv. 4" /></Veld>}
            <Veld label="Facturabel"><span className="check" style={{ minHeight: 34 }}><input type="checkbox" checked={f.facturabel} onChange={(e) => setF({ ...f, facturabel: e.target.checked })} /> Ja</span></Veld>
          </div>
          <Veld label="Omschrijving"><input value={f.omschrijving} onChange={(e) => setF({ ...f, omschrijving: e.target.value })} /></Veld>
        </div>
      )}
    </Modal>
  );
}

/** SPEC §11.3 – geschat · besteed · rest, met kleur bij overschrijding. */
export function Stand({ item, klein }: { item: ItemTijd; klein?: boolean }) {
  const s = itemStand(item);
  const toon = s.kleur === 'rood' ? 'rood' : s.kleur === 'oranje' ? 'oranje' : 'grijs';
  return (
    <span title={`Geschat ${s.geschat}u · besteed ${s.besteed}u · nog ${s.rest}u${s.afwijking ? ` · ${s.afwijking > 0 ? '+' : ''}${s.afwijking}u (${s.pct}%)` : ''}`}>
      <Badge toon={toon}>
        {klein ? `${s.besteed}/${s.geschat}u` : `${s.besteed}u van ${s.geschat}u · nog ${s.rest}u`}
        {s.afwijking > 0 && s.kleur !== 'groen' ? ` · +${s.pct}%` : ''}
      </Badge>
    </span>
  );
}
